import Flickity from "flickity";

export function setupGallery() {
	const sliderElement = document.querySelector<HTMLElement>(".gallery__slider");
	const maingrid = document.querySelector<HTMLElement>(".maingrid")!;
	const mqLandscape = window.matchMedia("(orientation: landscape)");

	if (sliderElement == null) {
		return;
	}

	let enableResize = sessionStorage.getItem("gallery:enableResize") !== "false";
	let maingridWidth = document.body.offsetWidth;
	let textColumnWidth = getTextColumnWidth();

	let slider = new Flickity(sliderElement, {
		setGallerySize: false,
		pageDots: false,
		percentPosition: true,
		prevNextButtons: false,
	});

	function resizeSlide() {
		let img = slider.cells[slider.selectedIndex].element.querySelector("img") as HTMLImageElement;
		if (img == null) {
			return;
		}

		let { naturalWidth, naturalHeight } = img;
		if (!img.complete) {
			// If image is not loaded yet, wait for it to load
			img.onload = () => {
				resizeSlide();
			};
			return;
		}

		let aspectRatio = naturalWidth / naturalHeight;
		if (mqLandscape.matches) {
			// Landscape mode: distribute space between first and third columns
			let availableWidth = maingridWidth - textColumnWidth;

			// Calculate max image width based on viewport height constraint
			// Image width cannot exceed available space (first + third columns)
			// First column gets the remaining space
			let maxImageWidthFromHeight = window.innerHeight * aspectRatio;
			let imageWidth = Math.min(maxImageWidthFromHeight, availableWidth);
			let firstColumnWidth = Math.max(0, availableWidth - imageWidth);

			// Update grid template columns and clear portait mode sizing
			maingrid.style.gridTemplateColumns = `${firstColumnWidth}px ${textColumnWidth}px ${imageWidth}px`;

			// Remove portrait mode styles
			img.style.width = "";
			img.style.height = "";
			sliderElement!.style.height = "";
		} else {
			// Portrait mode: image as wide as possible, height limited to 80% of viewport
			let maxHeight = window.innerHeight * 0.8;
			let maxImageWidthFromHeight = maxHeight * aspectRatio;
			let imageWidth = Math.min(maxImageWidthFromHeight, maingridWidth);

			img.style.width = `${imageWidth}px`;
			img.style.height = "auto";
			sliderElement!.style.height = `${imageWidth / aspectRatio}px`;
		}
	}

	function getTextColumnWidth() {
		let columnWidths = window.getComputedStyle(maingrid).gridTemplateColumns.split(" ").map(parseFloat);
		return columnWidths[1];
	}

	let prevButton = document.querySelector(".gallery__slider-control--prev");
	let nextButton = document.querySelector(".gallery__slider-control--next");
	let resizeButton = document.querySelector(".gallery__slider-control--resize");

	function updateControlStates() {
		if (slider.selectedIndex === 0) {
			prevButton?.setAttribute("disabled", "");
		} else {
			prevButton?.removeAttribute("disabled");
		}

		if (slider.selectedIndex === slider.cells.length - 1) {
			nextButton?.setAttribute("disabled", "");
		} else {
			nextButton?.removeAttribute("disabled");
		}

		resizeButton?.classList.toggle("is-locked", !enableResize);
	}

	slider.on("change", () => {
		if (enableResize || !mqLandscape.matches) {
			resizeSlide();
		}
		updateControlStates();
	});

	prevButton?.addEventListener("click", () => {
		slider.previous();
	});
	nextButton?.addEventListener("click", () => {
		slider.next();
	});
	resizeButton?.addEventListener("click", () => {
		enableResize = !enableResize;
		sessionStorage.setItem("gallery:enableResize", String(enableResize));
		resizeButton?.classList.toggle("is-locked", !enableResize);
	});

	// Update on window resize with debounce for performance
	let resizeTimeout: number;
	window.addEventListener("resize", () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = window.setTimeout(() => {
			maingridWidth = document.body.offsetWidth;
			textColumnWidth = getTextColumnWidth();
			resizeSlide();
		}, 100);
	});

	// Update on orientation change
	mqLandscape.addEventListener("change", e => {
		maingrid.style.gridTemplateColumns = "";
	});

	// Initial calculation
	maingrid.style.gridTemplateColumns = window.getComputedStyle(maingrid).gridTemplateColumns;
	if (enableResize) {
		// delay initial resize to prevent layout jump on page load
		window.setTimeout(() => {
			resizeSlide();
			slider.resize();
		}, 300);
	}
	updateControlStates();

	if (window.location.hash.length > 1) {
		let id = window.location.hash.substring(1);
		let index = slider.cells.findIndex(cell => cell.element.dataset["id"] === id);
		if (index !== -1) {
			slider.select(index, undefined, true);
			sliderElement.scrollIntoView({ behavior: "smooth" });
		}
	}
}
